import Service, { service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import type FeaturesService from './features.ts';
import { firstJsonApiError } from '../utils/parse-api-error.ts';
import { clickTarget, describeClickTarget } from '../utils/click-target.ts';
import {
  parseReplayCaptureWindow,
  sampledIntoWindow,
  type ReplayCaptureWindow,
} from '../utils/replay-capture.ts';
import { currentTraceId, startNewTrace } from 'tio-data/utils/traceparent';
import { ReplaySpriteInliner } from '../utils/replay-sprite-inliner.ts';
import { maskRecordedInputs, recordedElements } from '../utils/replay-privacy.ts';
import { pathOnly } from '../utils/url-privacy.ts';
import { replayRecorderOptions } from '../utils/replay-recorder-options.ts';

/** Identity attached to every event, already in the wire shape the telemetry API stores. */
export interface TelemetryUser {
  id: string;
  /** Digits or a UUID; any other shape is dropped server-side. */
  person_id?: string;
  role?: string;
  relationship_type?: string;
}

export interface TelemetryContext {
  company_id?: string;
  /** Opaque company-setting identifier used to compare employee cohorts. */
  company_setting_id?: string;
  meta?: Record<string, string>;
}

/** Deployment names the telemetry API stores; anything else is reported as `development`. */
export type TelemetryEnvironment = 'production' | 'staging' | 'vodka' | 'development' | 'test';

export interface TelemetryConfig {
  endpoint: string;
  app: string;
  token?: string;
  enabled?: boolean;
  /** Build identifier sent as `app_version` (the deploy's git SHA); truncated to 64 chars server-side. */
  appVersion?: string;
  /**
   * Deployment environment sent on every event. `production`, `staging`, `vodka` and `test` pass
   * through; any other value (`local`, `local-api-with-staging-db`, …) is sent as `development`.
   */
  environment?: string;
  /**
   * Error messages never reported, matched as a substring or RegExp, on top of the built-in browser
   * noise list (`ResizeObserver loop`, Ember's `TransitionAborted`, opaque cross-origin `Script error.`).
   */
  ignoreErrors?: Array<string | RegExp>;
  /**
   * Probability (0.0–1.0) that a `longtask` PerformanceObserver entry is sent as a `long_task` event.
   * Default 0 (off): the telemetry API rejects a whole batch containing an event type it does not
   * know, so enable this only where the server contract accepting `long_task` is deployed.
   */
  longTaskSampleRate?: number;
  /** Flush interval in ms (default: 10000) */
  flushInterval?: number;
  /** Max events per flush (default: 25) */
  batchSize?: number;
  /** Endpoint for session replay data (separate from RUM events) */
  replayEndpoint?: string;
  /** Enable rrweb session recording (default: false) */
  recordSession?: boolean;
  /**
   * Probability (0.0–1.0) of recording a given session when recordSession is
   * enabled. Defaults to 1.0 (record every session). Lower values materially
   * reduce storage cost on higher-volume apps (e.g. 0.1 = 10% of sessions).
   */
  replaySessionSampleRate?: number;
  /**
   * Replay capture mode (default: 'continuous').
   * - 'continuous': events flushed to server on every replayFlushInterval
   *   regardless of whether an error occurred. Useful for all-sessions review.
   * - 'on-error': events kept in a time-bounded ring buffer and only sent
   *   when an error fires. Dramatically reduces storage (often ~95%) while
   *   preserving the pre-error context needed to reproduce bugs.
   */
  replayMode?: 'continuous' | 'on-error';
  /**
   * Mask all DOM text in rrweb snapshots/mutations. Use for production capture
   * where class-based redaction may miss user-visible PII.
   */
  maskAllText?: boolean;
  /**
   * Size of the pre-error ring buffer in ms (default: 60000).
   * When replayMode is 'on-error', events older than this are evicted so
   * that, at error time, only the last N ms of context is flushed.
   */
  errorReplayBufferMs?: number;
  /**
   * After an error fires in 'on-error' mode, continue continuous flushing for
   * this many ms to capture the aftermath (navigation away, reload, etc.).
   * Default: 30000. Set to 0 to skip aftermath capture.
   */
  errorReplayAftermathMs?: number;
  /** Replay chunk flush interval in ms (default: 15000) */
  replayFlushInterval?: number;
}

/** The `ENV.telemetry` block each app's config/environment.js emits; the app adds `app` itself. */
export type TelemetryEnvironmentConfig = Omit<TelemetryConfig, 'app'>;

export interface TelemetryEvent {
  event_type: 'page_view' | 'error' | 'request' | 'action' | 'interaction' | 'long_task';
  app: string;
  app_version?: string;
  environment: TelemetryEnvironment;
  viewport_width: number;
  viewport_height: number;
  connection_type?: string;
  session_id: string;
  // The page's trace-id (shared with the page's outbound requests) so a RUM
  // event joins its backend trace. See tio-data/utils/traceparent.
  trace_id: string;
  timestamp: string;
  url: string;
  route?: string;
  referrer?: string;
  user_agent: string;
  user?: TelemetryUser;
  context?: TelemetryContext;
  page_view?: Record<string, number>;
  error?: Record<string, unknown>;
  request?: Record<string, unknown>;
  action?: Record<string, unknown>;
  interaction?: Record<string, unknown>;
  long_task?: Record<string, unknown>;
}

interface ReplayChunkPayload {
  session_id: string;
  app: string;
  chunk_index: number;
  person_id?: string;
  events: unknown[];
  timestamp: string;
}

/** What the first painted route was, for the page view of the load that reached it. */
interface LandingPage {
  route: string;
  url: string;
  trace_id: string;
}

interface ReplayUploadBody {
  body: BodyInit;
  contentEncoding?: 'gzip';
  payloadBytes: number;
}

interface ReplayUploadResponse {
  upload_url: string;
  key: string;
  headers?: Record<string, string>;
  complete?: Record<string, unknown>;
}

type ReplayStageResult<T> = { value: T } | { status?: number };

function replayRequestError(message: string, status?: number) {
  const error = new Error(message) as Error & { status?: number };
  error.status = status;
  return error;
}

function replayErrorStatus(error: unknown) {
  if (!(error instanceof Error)) return undefined;

  const status = (error as Error & { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

const KNOWN_ENVIRONMENTS: ReadonlySet<string> = new Set([
  'production',
  'staging',
  'vodka',
  'development',
  'test',
]);

const DEFAULT_IGNORED_ERRORS: ReadonlyArray<string | RegExp> = [
  /ResizeObserver loop/,
  /^TransitionAborted/,
  /^Script error\.?$/,
  /^TaskInstance '[^']*' was cancel/, // ember-concurrency dropping or restarting a task is flow control
];

interface NetworkInformationLike {
  effectiveType?: string;
  type?: string;
}

function isTelemetryEnvironment(value: string): value is TelemetryEnvironment {
  return KNOWN_ENVIRONMENTS.has(value);
}

/** Global and sticky RegExps carry `lastIndex` state across `test()` calls; match without it. */
function statelessPattern(pattern: string | RegExp) {
  if (typeof pattern === 'string' || !/[gy]/.test(pattern.flags)) return pattern;

  return new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, ''));
}

function connectionType() {
  const { connection } = navigator as Navigator & { connection?: NetworkInformationLike };
  return connection?.effectiveType ?? connection?.type;
}

interface ErrorDescription {
  message: string;
  stack?: string;
}

/**
 * Reduces whatever a route hook or promise rejected with to a message the API stores. A
 * JSON:API error carried anywhere WarpDrive puts one (top-level `errors[]` on an
 * `AggregateError`, or `content.errors` on a `RequestError`) wins as `[status] detail` so
 * triage sees the server's reason rather than the generic transport message; the stack of an
 * Error instance is always kept. Strings and plain objects with `message` pass through, and
 * anything else is stringified rather than dropped.
 */
function describeError(error: unknown): ErrorDescription {
  const stack = error instanceof Error ? { stack: error.stack } : {};
  const apiError = firstJsonApiError(error);
  if (apiError) {
    const text = [apiError.detail, apiError.title].find((value) => typeof value === 'string');
    const detail = typeof text === 'string' ? text : 'Request failed';
    return {
      message: apiError.status === undefined ? detail : `[${String(apiError.status)}] ${detail}`,
      ...stack,
    };
  }
  if (error instanceof Error) return { message: error.message, ...stack };
  if (typeof error === 'string') return { message: error };

  const message =
    error && typeof error === 'object' ? (error as { message?: unknown }).message : undefined;
  return { message: typeof message === 'string' ? message : String(error) };
}

/** Runs `callback` once the browser has painted the frame after the current one. */
function afterNextPaint(callback: () => void) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/**
 * First-party browser telemetry: web vitals, SPA page views, errors, slow requests, interactions,
 * sampled long tasks and rrweb session replay, batched to `/api/public/telemetry`.
 *
 * Key design: **passive observer** — uses standard browser APIs
 * (PerformanceObserver, addEventListener, keepalive fetch) without
 * monkey-patching any prototypes.
 *
 * Usage:
 *   // In application.ts beforeModel():
 *   this.telemetry.configure({ endpoint: '...', app: 'tio-admin', appVersion: ENV.version });
 *
 *   // In authenticated.ts beforeModel():
 *   this.telemetry.setUser({ id: user.id, person_id: user.person?.id });
 */
export default class TelemetryService extends Service {
  @service declare features: FeaturesService;

  private config: TelemetryConfig | null = null;
  private environment: TelemetryEnvironment = 'development';
  private ignoredErrors: ReadonlyArray<string | RegExp> = DEFAULT_IGNORED_ERRORS;
  private user: TelemetryUser | null = null;
  private context: TelemetryContext | null = null;
  private sessionId = '';
  private buffer: TelemetryEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private observers: PerformanceObserver[] = [];
  private boundErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private boundUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;
  private boundVisibilityChange: (() => void) | null = null;
  private boundClickHandler: ((event: MouseEvent) => void) | null = null;
  private boundKeyHandler: (() => void) | null = null;
  #sessionStartedAt = 0;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  // Set when the idle timer fired; the next interaction starts a new session.
  #sessionIdle = false;
  private boundScrollHandler: (() => void) | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private maxScrollDepth = 0;
  private currentRoute = '';
  private routeTransitionStartedAt: number | null = null;

  // rrweb session replay state
  private replayBuffer: unknown[] = [];
  private replayFlushTimer: ReturnType<typeof setInterval> | null = null;
  private replayAftermathTimeout: ReturnType<typeof setTimeout> | null = null;
  private replayStopFn: (() => void) | null = null;
  private replayChunkIndex: number | null = null;
  private replayErrorFlushed = false;
  private static readonly REPLAY_BUFFER_CAP = 500;
  private static readonly REPLAY_UPLOAD_ATTEMPTS = 3;
  /** A session ends after this long without a click, key press, scroll or route change. */
  static readonly SESSION_IDLE_MS = 15 * 60 * 1000;
  /** A session ends this long after it started, whatever the activity. */
  static readonly SESSION_MAX_MS = 4 * 60 * 60 * 1000;
  private static readonly SESSION_KEY = 'tio_telemetry_session';
  private static readonly SESSION_STARTED_KEY = 'tio_telemetry_session_started';
  private static readonly SESSION_ACTIVE_KEY = 'tio_telemetry_session_active';
  private static readonly REPLAY_CHUNK_INDEX_KEY = 'tio_replay_chunk_index';
  private static readonly CAPTURE_WINDOW_TIMEOUT_MS = 2_000;
  private static readonly CAPTURE_WINDOW_RECHECK_MS = 10 * 60_000;
  #buildReplayMode: TelemetryConfig['replayMode'];
  #captureWindowTimer: ReturnType<typeof setTimeout> | null = null;
  #spriteInliner = new ReplaySpriteInliner();
  #recordedElements = recordedElements();
  #firstRoutePainted: Promise<LandingPage | null> = Promise.resolve(null);
  #resolveFirstRoutePainted: (landing: LandingPage) => void = () => undefined;
  #landing: LandingPage | null = null;
  #pageHiding: Promise<void> = Promise.resolve();
  #resolvePageHiding: () => void = () => undefined;
  #hiding = false;
  #boundPageHide: (() => void) | null = null;
  // Chunks whose upload has started but not finished: an unload would lose them too.
  private inflightReplayChunks = new Map<number, ReplayChunkPayload>();
  // Set once uploads are known to be lost for this page load; nothing else is recorded or sent.
  private replayUploadsBroken = false;
  private replayUploadFailures = 0;
  private static readonly PENDING_REPLAY_CHUNKS_KEY = 'tio_replay_pending_chunks';
  // How long the first snapshot waits for the router before settling for whatever has rendered.
  private static readonly RENDER_WAIT_MS = 5_000;
  // How long a page view waits for its route; a page left sooner sends it as it goes.
  private static readonly ROUTE_WAIT_MS = 60_000;

  configure = (config: TelemetryConfig) => {
    if (this.config) return; // Already configured

    this.config = {
      flushInterval: 10_000,
      batchSize: 25,
      enabled: true,
      ...config,
    };

    if (!this.config.enabled) return;

    const environment = this.config.environment ?? '';
    this.environment = isTelemetryEnvironment(environment) ? environment : 'development';
    this.ignoredErrors = [
      ...DEFAULT_IGNORED_ERRORS,
      ...(this.config.ignoreErrors ?? []).map(statelessPattern),
    ];
    this.sessionId = this.resumeOrStartSession();
    this.#armIdleTimer();
    this.#firstRoutePainted = new Promise((resolve) => (this.#resolveFirstRoutePainted = resolve));
    this.#pageHiding = new Promise((resolve) => (this.#resolvePageHiding = resolve));
    this.startObservers();
    this.startFlushTimer();

    if (this.config.recordSession && this.config.replayEndpoint) {
      this.uploadPendingReplayChunks();
      void this.startReplayCapture();
    }
  };

  // A session sampled into an API-set capture window records continuously; every other
  // session keeps the build's replay mode and sample rate, so error replays are never lost.
  private async startReplayCapture() {
    const window = await this.fetchReplayCaptureWindow();
    if (window && sampledIntoWindow(window)) {
      this.#recordContinuously(window);
    } else if (this.shouldRecordSession()) {
      void this.startSessionRecording();
    }
  }

  #recordContinuously(window: ReplayCaptureWindow) {
    if (!this.config) return;
    this.#buildReplayMode = this.config.replayMode;
    this.config = { ...this.config, replayMode: 'continuous' };
    void this.startSessionRecording();
    this.#scheduleCaptureWindowCheck(window);
  }

  // A long-lived tab re-reads the window when it ends and at least every ten minutes, so an
  // ended or withdrawn window hands the tab back to the build's replay mode.
  #scheduleCaptureWindowCheck(window: ReplayCaptureWindow) {
    const untilEnd = Math.max(window.endsAt.getTime() - Date.now(), 0);
    this.#captureWindowTimer = setTimeout(
      () => void this.#recheckCaptureWindow(window),
      Math.min(untilEnd, TelemetryService.CAPTURE_WINDOW_RECHECK_MS)
    );
  }

  async #recheckCaptureWindow(window: ReplayCaptureWindow) {
    this.#captureWindowTimer = null;
    const current = await this.fetchReplayCaptureWindow();
    if (current?.endsAt.getTime() === window.endsAt.getTime()) {
      this.#scheduleCaptureWindowCheck(current);
      return;
    }
    if (current && sampledIntoWindow(current)) {
      this.#scheduleCaptureWindowCheck(current);
      return;
    }
    this.#returnToBuildReplayMode();
  }

  #returnToBuildReplayMode() {
    if (this.replayFlushTimer) {
      clearInterval(this.replayFlushTimer);
      this.replayFlushTimer = null;
    }
    this.flushReplayBuffer();
    this.stopSessionRecording();
    if (!this.config) return;
    this.config = { ...this.config, replayMode: this.#buildReplayMode };
    if (this.shouldRecordSession()) void this.startSessionRecording();
  }

  private async fetchReplayCaptureWindow() {
    const config = this.config;
    if (!config) return null;

    const headers: Record<string, string> = {};
    if (config.token) headers['X-Telemetry-Token'] = config.token;
    const url = `${config.endpoint.replace(/\/$/, '')}/config?app=${encodeURIComponent(config.app)}`;
    try {
      const response = await fetch(url, {
        headers,
        credentials: 'omit',
        signal: AbortSignal.timeout(TelemetryService.CAPTURE_WINDOW_TIMEOUT_MS),
      });
      return response.ok ? parseReplayCaptureWindow(await response.json()) : null;
    } catch {
      return null;
    }
  }

  private shouldRecordSession(): boolean {
    const rate = this.config?.replaySessionSampleRate;
    if (rate === undefined || rate === null) return true;
    if (rate <= 0) return false;
    if (rate >= 1) return true;

    // Persist the sampling decision so it is stable across reloads within the same session
    const storedDecision = sessionStorage.getItem('tio_replay_sampled');
    if (storedDecision !== null) {
      return storedDecision === 'true';
    }
    const sampled = Math.random() < rate;
    sessionStorage.setItem('tio_replay_sampled', String(sampled));
    return sampled;
  }

  setUser = (user: TelemetryUser) => {
    this.user = user;
  };

  setContext = (context: TelemetryContext) => {
    this.context = context;
  };

  setRoute = (routeName: string) => {
    this.currentRoute = routeName;
  };

  /**
   * Marks the start of an SPA transition; call from the router's `routeWillChange` handler.
   * The next `trackRouteChange` measures `loading_time` from this mark, so it covers the route
   * hooks as well as rendering. Redirects re-enter here and keep the first mark.
   */
  trackRouteWillChange = () => {
    this.routeTransitionStartedAt ??= performance.now();
  };

  /**
   * Track an SPA route transition as a page_view event.
   * Call from the router's `routeDidChange` handler.
   *
   * On the first call (initial page load) this only sets the route for
   * enrichment — the PerformanceObserver navigation entry already emits
   * that page_view. Subsequent calls emit an SPA transition page_view once
   * the new route has painted, carrying `loading_time` in ms measured from
   * the `trackRouteWillChange` mark (or from this call when none was set).
   */
  trackRouteChange = (routeName: string) => {
    const isInitial = !this.currentRoute;
    const startedAt = this.routeTransitionStartedAt ?? performance.now();
    this.routeTransitionStartedAt = null;
    this.currentRoute = routeName;
    this.maxScrollDepth = 0;
    // The page view below is the new session's first when this transition rotated it.
    if (!isInitial) this.#noteActivity({ landing: false });

    if (isInitial) {
      // Captured at paint: a second transition inside the vitals delay would otherwise lend the
      // landing page's view its own route, URL and trace.
      afterNextPaint(() => {
        this.#landing = {
          route: routeName,
          url: pathOnly(window.location.href),
          trace_id: currentTraceId(),
        };
        this.#resolveFirstRoutePainted(this.#landing);
      });
      return;
    }

    // New page = new trace, so the page_view and the new route's subsequent
    // requests/interactions group under one trace. (The initial page load
    // reuses the lazily-minted trace its boot requests already share.)
    startNewTrace();
    // Captured now: a further transition inside the two-frame wait would otherwise
    // re-attribute this page_view to the next route's trace and URL.
    const trace_id = currentTraceId();
    const url = pathOnly(window.location.href);
    afterNextPaint(() => {
      this.push({
        event_type: 'page_view',
        route: routeName,
        trace_id,
        url,
        page_view: { spa_transition: 1, loading_time: Math.round(performance.now() - startedAt) },
      });
    });
  };

  trackAction = (name: string, target?: string) => {
    this.push({
      event_type: 'action',
      action: { name, target },
    });
  };

  /**
   * Records a named duration the app measured itself (for example the span from the
   * login submit to the dashboard's first frame). Sent as an `action` event carrying
   * `duration_ms`; the telemetry API keeps that field only for names it whitelists.
   *
   * @param name timing name, e.g. `login_to_dashboard`.
   * @param durationMs measured duration in milliseconds; rounded before sending.
   */
  trackTiming = (name: string, durationMs: number) => {
    this.push({
      event_type: 'action',
      action: { name, duration_ms: Math.round(durationMs) },
    });
  };

  /**
   * Reports an error the app caught itself. Non-Error values are normalised by
   * `describeError`; messages matching the ignore list are dropped before buffering, so they
   * trigger no error replay either.
   */
  trackError = (error: unknown, context?: Record<string, unknown>) => {
    this.pushError({ ...describeError(error), type: 'manual', ...context });
  };

  /**
   * Reports an error a route hook raised during a transition. The event's `route` is the
   * transition's destination rather than the route still on screen, and the error is typed
   * `route` so triage can separate failed navigations from runtime exceptions.
   */
  trackRouteError = (error: unknown, transition: Transition) => {
    this.pushError({ ...describeError(error), type: 'route' }, transition.to?.name);
  };

  /**
   * Reports a failed API request the request layer surfaced. Sends the URL without its query
   * string or fragment, the method, HTTP status and duration in ms (rounded); never the payload.
   */
  trackRequest = (request: { url?: string; method: string; status: number; duration?: number }) => {
    const { method, status, duration } = request;
    const url = pathOnly(request.url);

    this.push({
      event_type: 'request',
      request: {
        method,
        status,
        ...(url === undefined ? {} : { url }),
        ...(duration === undefined ? {} : { duration: Math.round(duration) }),
      },
    });
  };

  // -- Lifecycle --

  willDestroy() {
    super.willDestroy();
    this.flush();
    // In on-error mode, only flush replay if an error actually fired;
    // otherwise nothing should leave the browser.
    if (this.config?.replayMode !== 'on-error' || this.replayErrorFlushed) {
      this.flushReplayBuffer();
    }
    this.stopObservers();
    this.stopSessionRecording();
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.replayFlushTimer) {
      clearInterval(this.replayFlushTimer);
      this.replayFlushTimer = null;
    }
    if (this.replayAftermathTimeout) {
      clearTimeout(this.replayAftermathTimeout);
      this.replayAftermathTimeout = null;
    }
    if (this.#captureWindowTimer) {
      clearTimeout(this.#captureWindowTimer);
      this.#captureWindowTimer = null;
    }
  }

  private pushError(details: Record<string, unknown>, route?: string) {
    if (this.shouldIgnoreError(details['message'])) return;
    // An idle tab's error is not recorded, so it must not start an error replay either.
    if (this.#sessionIdle) return;

    this.push({ event_type: 'error', route, error: details });
    this.triggerErrorReplay();
  }

  private shouldIgnoreError(message: unknown) {
    if (typeof message !== 'string') return false;

    return this.ignoredErrors.some((pattern) =>
      typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message)
    );
  }

  private push(
    partial: Omit<
      TelemetryEvent,
      | 'app'
      | 'app_version'
      | 'environment'
      | 'viewport_width'
      | 'viewport_height'
      | 'connection_type'
      | 'session_id'
      | 'trace_id'
      | 'timestamp'
      | 'url'
      | 'user_agent'
    > &
      Partial<Pick<TelemetryEvent, 'trace_id' | 'url'>>
  ) {
    if (!this.config?.enabled) return;
    // Nothing between a session's expiry and the next interaction counts.
    if (this.#sessionIdle) return;

    const event: TelemetryEvent = {
      ...partial,
      app: this.config.app,
      app_version: this.config.appVersion || undefined,
      environment: this.environment,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      connection_type: connectionType(),
      session_id: this.sessionId,
      trace_id: partial.trace_id ?? currentTraceId(),
      timestamp: new Date().toISOString(),
      url: pathOnly(partial.url ?? window.location.href),
      route: partial.route ?? (this.currentRoute || undefined),
      referrer: pathOnly(document.referrer) || undefined,
      user_agent: navigator.userAgent,
    };

    if (this.user) {
      event.user = this.user;
    }

    if (this.context) {
      event.context = this.context;
    }

    this.buffer.push(event);

    if (this.buffer.length >= (this.config.batchSize ?? 25)) {
      this.flush();
    }
  }

  private flush = () => {
    if (!this.config?.enabled || this.buffer.length === 0) return;

    const events = this.buffer.splice(0, this.config.batchSize ?? 25);
    const payload = JSON.stringify(events);
    const url = this.config.endpoint;

    this.sendViaFetch(url, payload);
  };

  private sendViaFetch(url: string, payload: string) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.config?.token) {
      headers['X-Telemetry-Token'] = this.config.token;
    }

    // Fire and forget — telemetry should never block the app. We use the Fetch API
    // with `keepalive: true` so the request can continue during page unload or
    // navigation, similar to sendBeacon-style behavior but with explicit control
    // over headers and credentials.
    // Note: credentials: 'omit' is required because the API uses a wildcard
    // Access-Control-Allow-Origin header, which is incompatible with
    // credentials: 'include'. The endpoint authenticates via X-Telemetry-Token,
    // not cookies.
    fetch(url, {
      method: 'POST',
      headers,
      body: payload,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Silently swallow — telemetry failures are not actionable for users
    });
  }

  private startFlushTimer() {
    this.flushTimer = setInterval(this.flush, this.config?.flushInterval ?? 10_000);

    // Also flush on page hide (tab switch, navigation, close)
    this.boundVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    this.#boundPageHide = () => {
      this.#hiding = true;
      this.#resolvePageHiding();
      this.stashReplayChunks();
    };
    window.addEventListener('pagehide', this.#boundPageHide);
  }

  private startObservers() {
    this.observeWebVitals();
    this.observeErrors();
    this.observeResources();
    this.observeInteractions();
    this.observeLongTasks();
  }

  private stopObservers() {
    for (const observer of this.observers) {
      observer.disconnect();
    }
    this.observers = [];

    if (this.boundErrorHandler) {
      window.removeEventListener('error', this.boundErrorHandler);
    }
    if (this.boundUnhandledRejection) {
      window.removeEventListener('unhandledrejection', this.boundUnhandledRejection);
    }
    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    }
    if (this.#boundPageHide) {
      window.removeEventListener('pagehide', this.#boundPageHide);
    }
    if (this.boundKeyHandler) {
      document.removeEventListener('keydown', this.boundKeyHandler, true);
      this.boundKeyHandler = null;
    }
    if (this.boundClickHandler) {
      document.removeEventListener('click', this.boundClickHandler, true);
    }
    if (this.boundScrollHandler) {
      window.removeEventListener('scroll', this.boundScrollHandler);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  /**
   * Core Web Vitals via PerformanceObserver.
   * Multiple observers are spec-compliant — no conflict with any other tool on the page.
   */
  private observeWebVitals() {
    const vitals: Record<string, number> = {};
    let navigationSent = false;

    // LCP (Largest Contentful Paint)
    this.safeObserve('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (last) {
        vitals['lcp'] = Math.round(last.startTime);
      }
    });

    // FCP (First Contentful Paint)
    this.safeObserve('paint', (entries) => {
      for (const entry of entries) {
        if (entry.name === 'first-contentful-paint') {
          vitals['fcp'] = Math.round(entry.startTime);
        }
      }
    });

    // CLS (Cumulative Layout Shift)
    this.safeObserve('layout-shift', (entries) => {
      let clsValue = vitals['cls'] ?? 0;
      for (const entry of entries) {
        const layoutEntry = entry as PerformanceEntry & { hadRecentInput: boolean; value: number };
        if (!layoutEntry.hadRecentInput) {
          clsValue += layoutEntry.value;
        }
      }
      vitals['cls'] = Math.round(clsValue * 1000) / 1000;
    });

    // INP (Interaction to Next Paint)
    this.safeObserve('event', (entries) => {
      for (const entry of entries) {
        const eventEntry = entry as PerformanceEntry & { duration: number };
        const duration = eventEntry.duration;
        if (!vitals['inp'] || duration > vitals['inp']) {
          vitals['inp'] = duration;
        }
      }
    });

    // Navigation timing (TTFB, DOM interactive, load time)
    this.safeObserve('navigation', (entries) => {
      if (navigationSent) return;
      navigationSent = true;

      const nav = entries[0] as PerformanceNavigationTiming;
      if (nav) {
        vitals['ttfb'] = Math.round(nav.responseStart - nav.requestStart);
        vitals['dom_interactive'] = Math.round(nav.domInteractive);
        vitals['dom_complete'] = Math.round(nav.domComplete);
        vitals['load_time'] = Math.round(nav.loadEventEnd);
      }

      void this.sendNavigationPageView(vitals);
    });
  }

  // Vitals settle for a few seconds after the navigation entry, and the route is only known once
  // the router has reported it: a page view sent at load time would carry no route at all. A
  // page left before then sends it as it goes, with whatever route is known by then.
  private async sendNavigationPageView(vitals: Record<string, number>) {
    const settled = Promise.all([
      delay(3_000),
      this.firstRoutePaintedOrTimeout(TelemetryService.ROUTE_WAIT_MS),
    ]);
    await Promise.race([settled, this.#pageHiding]);
    this.push({ event_type: 'page_view', ...(this.#landing ?? {}), page_view: { ...vitals } });
    if (this.#hiding) this.flush();
  }

  private firstRoutePaintedOrTimeout(waitMs: number): Promise<LandingPage | null> {
    return Promise.race([this.#firstRoutePainted, delay(waitMs).then(() => null)]);
  }

  /**
   * Capture unhandled JS errors and promise rejections.
   */
  private observeErrors() {
    this.boundErrorHandler = (event: ErrorEvent) => {
      this.pushError({
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'uncaught',
      });
    };

    this.boundUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.pushError({
        message: reason?.message || String(reason),
        stack: reason?.stack,
        type: 'unhandled_rejection',
      });
    };

    window.addEventListener('error', this.boundErrorHandler);
    window.addEventListener('unhandledrejection', this.boundUnhandledRejection);
  }

  /**
   * Track slow network requests via Resource Timing API.
   * Only captures requests > 1s duration. Does not detect HTTP status failures
   * (Resource Timing API doesn't expose status codes).
   */
  private observeResources() {
    this.safeObserve('resource', (entries) => {
      for (const entry of entries) {
        const resource = entry as PerformanceResourceTiming;
        const duration = resource.duration;

        // Only track slow requests (>1s)
        if (duration > 1000 && !this.isOwnRequest(resource.name)) {
          this.push({
            event_type: 'request',
            request: {
              url: pathOnly(resource.name),
              duration: Math.round(duration),
              initiator_type: resource.initiatorType,
            },
          });
        }
      }
    });
  }

  // The batches and replay chunks this service uploads, including the presigned bucket PUTs,
  // whose object keys sit at the bucket's root; the workbench's own replay API lives under /api.
  private isOwnRequest(url: string) {
    const own = [this.config?.endpoint, this.config?.replayEndpoint].filter(Boolean) as string[];
    if (own.some((prefix) => url.startsWith(prefix))) return true;
    try {
      return new URL(url).pathname.startsWith('/session-replays/');
    } catch {
      return false;
    }
  }

  /**
   * Main-thread blocks of 50 ms or more as `long_task` events, sampled per entry so a janky
   * route cannot exhaust the batch quota on its own.
   */
  private observeLongTasks() {
    const rate = this.config?.longTaskSampleRate ?? 0;
    if (rate <= 0) return;

    this.safeObserve('longtask', (entries) => {
      for (const entry of entries) {
        if (Math.random() >= rate) continue;

        this.push({
          event_type: 'long_task',
          long_task: {
            duration: Math.round(entry.duration),
            route: this.currentRoute || undefined,
          },
        });
      }
    });
  }

  /**
   * Capture user interactions: clicks (with element info and coordinates)
   * and scroll depth (debounced, tracks max depth per page).
   */
  private observeInteractions() {
    // Click tracking — capture on the capture phase to get all clicks
    this.boundClickHandler = (event: MouseEvent) => {
      this.#noteActivity();
      if (!(event.target instanceof Element)) return;

      const target = clickTarget(event.target);

      this.push({
        event_type: 'interaction',
        interaction: {
          type: 'click',
          selector: describeClickTarget(target),
          tag: target.tagName.toLowerCase(),
          x: event.clientX,
          y: event.clientY,
          page_x: event.pageX,
          page_y: event.pageY,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
        },
      });
    };
    document.addEventListener('click', this.boundClickHandler, true);
    this.boundKeyHandler = () => this.#noteActivity();
    document.addEventListener('keydown', this.boundKeyHandler, true);

    // Scroll depth tracking — debounced, emits when user stops scrolling
    this.boundScrollHandler = () => {
      this.#noteActivity();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const viewportHeight = window.innerHeight;
      const scrollPercent =
        docHeight > viewportHeight
          ? Math.round(((scrollTop + viewportHeight) / docHeight) * 100)
          : 100;

      if (scrollPercent > this.maxScrollDepth) {
        this.maxScrollDepth = scrollPercent;
      }

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.push({
          event_type: 'interaction',
          interaction: {
            type: 'scroll',
            scroll_depth: this.maxScrollDepth,
            scroll_y: Math.round(scrollTop),
            doc_height: Math.round(docHeight),
            viewport_height: viewportHeight,
          },
        });
      }, 1000);
    };
    window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
  }

  /**
   * Wait until the app has rendered meaningful DOM content.
   * rrweb's FullSnapshot captures the DOM at record() time, so calling it
   * before the framework renders produces an empty snapshot.
   */
  // The first snapshot is taken once the first route has painted; without a router signal it
  // settles for a body with content, which the index.html shell alone does not have.
  private async waitForRenderedDOM(): Promise<void> {
    const painted = await Promise.race([
      this.#firstRoutePainted.then(() => true),
      delay(TelemetryService.RENDER_WAIT_MS).then(() => false),
    ]);
    if (!painted) await this.waitForBodyContent();
  }

  private waitForBodyContent(): Promise<void> {
    return new Promise((resolve) => {
      let elapsed = 0;
      const poll = () => {
        // body has real content once the framework renders (not just script/noscript)
        if (document.body.childElementCount > 2 || elapsed >= 5000) {
          resolve();
        } else {
          elapsed += 100;
          setTimeout(poll, 100);
        }
      };
      if (document.readyState === 'complete') {
        poll();
      } else {
        window.addEventListener('load', poll, { once: true });
      }
    });
  }

  private async startSessionRecording() {
    if (this.replayUploadsBroken) return;
    try {
      const { record } = await import('rrweb');

      // Wait for the app to render before capturing the initial FullSnapshot.
      // Ember.js renders asynchronously after DOM load — recording too early
      // captures only the raw index.html template (empty body).
      await this.waitForRenderedDOM();
      // A lockout raised while the import or the render wait was pending must not start a recorder.
      if (this.replayUploadsBroken) return;

      const mode = this.config?.replayMode ?? 'continuous';
      const bufferMs = this.config?.errorReplayBufferMs ?? 60_000;

      this.replayStopFn =
        record({
          emit: (event: unknown) => {
            this.onReplayEvent(event, mode, bufferMs);
          },
          ...replayRecorderOptions({ mode, bufferMs, maskAllText: this.config?.maskAllText }),
        }) ?? null;

      if (mode === 'continuous') {
        // Flush immediately to capture the initial Meta + FullSnapshot events.
        // rrweb emits these synchronously during record() — if we wait for the
        // interval timer, they can be lost to HMR or navigation.
        this.flushReplayBuffer();

        // Flush replay data on a separate interval (larger chunks)
        this.replayFlushTimer = setInterval(
          () => this.flushReplayBuffer(),
          this.config?.replayFlushInterval ?? 15_000
        );
      }
      // In on-error mode we do NOT flush proactively. triggerErrorReplay()
      // runs on error and ships the ring buffer + starts aftermath flushing.
    } catch {
      // rrweb import failed — skip silently
    }
  }

  /**
   * Event ingestion from rrweb. Separated so continuous mode retains its
   * simple bounded cap while on-error mode runs a time-windowed ring buffer.
   */
  private onReplayEvent(event: unknown, mode: 'continuous' | 'on-error', bufferMs: number) {
    if (this.replayUploadsBroken) return;
    if (mode === 'on-error' && !this.replayErrorFlushed) {
      // Time-bounded ring buffer: evict events older than the window.
      this.replayBuffer.push(event);
      const cutoff = Date.now() - bufferMs;
      while (this.replayBuffer.length > 0) {
        const head = this.replayBuffer[0] as { timestamp?: number } | undefined;
        if (head?.timestamp && head.timestamp < cutoff) {
          this.replayBuffer.shift();
        } else {
          break;
        }
      }
      // Safety cap to prevent runaway growth if timestamps are absent.
      while (this.replayBuffer.length > TelemetryService.REPLAY_BUFFER_CAP * 4) {
        this.replayBuffer.shift();
      }
      return;
    }

    // Continuous mode (or on-error after the trigger): fixed cap, drop newest
    // once full so we don't inflate memory between flushes.
    if (this.replayBuffer.length < TelemetryService.REPLAY_BUFFER_CAP) {
      this.replayBuffer.push(event);
    }
  }

  /**
   * Called when an error fires. If we're in on-error mode and haven't yet
   * flushed, ship the ring buffer as chunk 0 and continue flushing for the
   * aftermath window to capture the user's reaction (retry, navigate away).
   * No-op in continuous mode (flushes already happen on interval).
   */
  private triggerErrorReplay() {
    if (this.config?.replayMode !== 'on-error') return;
    if (this.replayErrorFlushed) return;
    if (!this.config?.replayEndpoint) return;

    this.replayErrorFlushed = true;
    this.flushReplayBuffer();

    const aftermath = this.config?.errorReplayAftermathMs ?? 30_000;
    if (aftermath <= 0) {
      this.stopSessionRecording();
      return;
    }

    const flushInterval = this.config?.replayFlushInterval ?? 15_000;
    this.replayFlushTimer = setInterval(() => this.flushReplayBuffer(), flushInterval);

    this.replayAftermathTimeout = setTimeout(() => {
      if (this.replayFlushTimer) {
        clearInterval(this.replayFlushTimer);
        this.replayFlushTimer = null;
      }
      this.flushReplayBuffer();
      this.stopSessionRecording();
      this.replayAftermathTimeout = null;
    }, aftermath);
  }

  private stopSessionRecording() {
    if (this.replayStopFn) {
      this.replayStopFn();
      this.replayStopFn = null;
    }
  }

  private flushReplayBuffer = () => {
    const payload = this.takeReplayChunk();
    if (payload) void this.uploadReplayChunk(payload, this.replayHeaders());
  };

  private takeReplayChunk(): ReplayChunkPayload | null {
    if (
      this.replayUploadsBroken ||
      !this.config?.replayEndpoint ||
      this.replayBuffer.length === 0
    ) {
      return null;
    }

    return {
      session_id: this.sessionId,
      app: this.config.app,
      chunk_index: this.nextReplayChunkIndex(),
      person_id: this.user?.person_id,
      events: this.replayBuffer.splice(0),
      timestamp: new Date().toISOString(),
    };
  }

  private replayHeaders() {
    const headers: Record<string, string> = {};
    if (this.config?.token) headers['X-Telemetry-Token'] = this.config.token;
    return headers;
  }

  // A page left before the next interval flush would lose everything recorded since the last
  // one, and an upload cannot finish during unload; the unsent buffer and the chunks still
  // uploading wait in sessionStorage for the session's next page load instead. An on-error
  // ring buffer is not due for upload.
  private stashReplayChunks() {
    const pending = [...this.inflightReplayChunks.values()];
    const due = this.config?.replayMode !== 'on-error' || this.replayErrorFlushed;
    const current = due ? this.takeReplayChunk() : null;
    if (current) {
      maskRecordedInputs(current.events, this.#recordedElements);
      pending.push(current);
    }
    if (pending.length === 0) return;
    pending.sort((a, b) => a.chunk_index - b.chunk_index);
    try {
      sessionStorage.setItem(TelemetryService.PENDING_REPLAY_CHUNKS_KEY, JSON.stringify(pending));
    } catch {
      // Too large for the store, or no store: the visit's last seconds are lost, as before.
    }
  }

  private uploadPendingReplayChunks() {
    if (this.replayUploadsBroken) return;
    for (const payload of this.takePendingReplayChunks()) {
      if (payload.session_id !== this.sessionId || !Array.isArray(payload.events)) continue;
      void this.uploadReplayChunk(payload, this.replayHeaders());
    }
  }

  private takePendingReplayChunks(): ReplayChunkPayload[] {
    try {
      const stored = sessionStorage.getItem(TelemetryService.PENDING_REPLAY_CHUNKS_KEY);
      sessionStorage.removeItem(TelemetryService.PENDING_REPLAY_CHUNKS_KEY);
      const parsed = JSON.parse(stored ?? '[]') as unknown;
      return Array.isArray(parsed) ? (parsed as ReplayChunkPayload[]) : [];
    } catch {
      return [];
    }
  }

  // A chunk is stored under its session id and index, and the session id outlives a page load, so
  // the counter has to as well: restarting at 0 would overwrite the recording made before the load.
  private nextReplayChunkIndex() {
    this.replayChunkIndex ??= this.storedReplayChunkIndex();
    const chunkIndex = this.replayChunkIndex++;
    try {
      sessionStorage.setItem(
        TelemetryService.REPLAY_CHUNK_INDEX_KEY,
        JSON.stringify({ session: this.sessionId, next: this.replayChunkIndex })
      );
    } catch {
      // Without storage the in-memory counter still numbers this page load's chunks.
    }
    return chunkIndex;
  }

  private storedReplayChunkIndex() {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem(TelemetryService.REPLAY_CHUNK_INDEX_KEY) ?? 'null'
      ) as { session?: unknown; next?: unknown } | null;
      if (
        stored?.session === this.sessionId &&
        Number.isInteger(stored.next) &&
        (stored.next as number) >= 0
      ) {
        return stored.next as number;
      }
    } catch {
      // An unreadable store counts from 0, as a session without a stored counter does.
    }
    return 0;
  }

  private async uploadReplayChunk(
    payload: ReplayChunkPayload,
    extraHeaders: Record<string, string>
  ) {
    if (this.replayUploadsBroken) return;
    this.inflightReplayChunks.set(payload.chunk_index, payload);
    try {
      await this.uploadReplayChunkStages(payload, extraHeaders);
    } finally {
      this.inflightReplayChunks.delete(payload.chunk_index);
    }
  }

  private async uploadReplayChunkStages(
    payload: ReplayChunkPayload,
    extraHeaders: Record<string, string>
  ) {
    maskRecordedInputs(payload.events, this.#recordedElements);
    try {
      await this.#spriteInliner.inline(payload.events);
    } catch {
      // The chunk still uploads as recorded; only its sprite icons play back blank.
    }

    let body: ReplayUploadBody;
    try {
      body = await this.replayUploadBody(JSON.stringify(payload));
    } catch {
      this.reportReplayUploadFailure('presign');
      return;
    }
    // The preparation above is asynchronous; a lockout raised meanwhile must not start a presign.
    if (this.replayUploadsBroken) return;

    const presign = await this.retryReplayStage(() =>
      this.presignReplayChunk(payload, body, extraHeaders)
    );
    if (this.replayUploadsBroken) return;
    if (!('value' in presign)) {
      this.reportReplayUploadFailure('presign', presign.status);
      return;
    }

    // Reuse the same presigned URL for PUT retries. A new URL is minted only
    // when the presign stage itself needs another attempt.
    const put = await this.retryReplayStage(() => this.putReplayChunk(presign.value, body));
    if (this.replayUploadsBroken) return;
    if (!('value' in put)) {
      this.reportReplayUploadFailure('s3_put', put.status);
      return;
    }

    const complete = await this.retryReplayStage(() =>
      this.completeReplayChunk(presign.value, extraHeaders)
    );
    if (!('value' in complete)) {
      this.reportReplayUploadFailure('complete', complete.status);
      return;
    }
    this.replayUploadFailures = 0;
  }

  private async retryReplayStage<T>(operation: () => Promise<T>): Promise<ReplayStageResult<T>> {
    let status: number | undefined;

    for (let attempt = 1; attempt <= TelemetryService.REPLAY_UPLOAD_ATTEMPTS; attempt++) {
      try {
        return { value: await operation() };
      } catch (error) {
        status = replayErrorStatus(error);
        if (attempt === TelemetryService.REPLAY_UPLOAD_ATTEMPTS) return { status };

        await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** (attempt - 1)));
        if (this.replayUploadsBroken) return { status };
      }
    }

    return { status };
  }

  private async presignReplayChunk(
    payload: ReplayChunkPayload,
    body: ReplayUploadBody,
    extraHeaders: Record<string, string>
  ): Promise<ReplayUploadResponse> {
    const uploadResponse = await fetch(this.config!.replayEndpoint!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      body: JSON.stringify({
        session_id: payload.session_id,
        app: payload.app,
        chunk_index: payload.chunk_index,
        person_id: payload.person_id,
        event_count: payload.events.length,
        payload_bytes: body.payloadBytes,
        content_encoding: body.contentEncoding,
        meta: this.replayMeta(payload.events),
      }),
      keepalive: true,
      credentials: 'omit',
    });
    if (!uploadResponse.ok) {
      throw replayRequestError(`presign returned ${uploadResponse.status}`, uploadResponse.status);
    }

    return (await uploadResponse.json()) as ReplayUploadResponse;
  }

  private async putReplayChunk(upload: ReplayUploadResponse, body: ReplayUploadBody) {
    const putResponse = await fetch(upload.upload_url, {
      method: 'PUT',
      headers: upload.headers ?? { 'Content-Type': 'application/json' },
      body: body.body,
      credentials: 'omit',
    });
    if (!putResponse.ok) {
      throw replayRequestError(`S3 PUT returned ${putResponse.status}`, putResponse.status);
    }
  }

  private async completeReplayChunk(
    upload: ReplayUploadResponse,
    extraHeaders: Record<string, string>
  ) {
    const completeResponse = await fetch(this.config!.replayEndpoint!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...extraHeaders },
      body: JSON.stringify(upload.complete ?? { uploaded_key: upload.key }),
      keepalive: true,
      credentials: 'omit',
    });
    if (!completeResponse.ok) {
      throw replayRequestError(
        `completion returned ${completeResponse.status}`,
        completeResponse.status
      );
    }
  }

  // A bucket this origin cannot reach (a local build pointed at a shared environment) would
  // otherwise fail every chunk for the rest of the session and count each one as an error. A lone
  // 5xx or network failure is reported and recording continues; a 4xx, or a second exhausted
  // chunk in a row, ends it for the page load.
  private reportReplayUploadFailure(stage: 'presign' | 's3_put' | 'complete', status?: number) {
    if (this.replayUploadsBroken) return;
    this.replayUploadFailures += 1;
    this.push({
      event_type: 'action',
      action: { name: 'replay_upload_failed', target: status ? `${stage} ${status}` : stage },
    });
    const clientRejected = status !== undefined && status >= 400 && status < 500;
    if (clientRejected || this.replayUploadFailures >= 2) {
      this.replayUploadsBroken = true;
      this.stopSessionRecording();
      if (this.replayFlushTimer) {
        clearInterval(this.replayFlushTimer);
        this.replayFlushTimer = null;
      }
      this.replayBuffer = [];
      this.inflightReplayChunks.clear();
    }
    this.flush();
  }

  private async replayUploadBody(payload: string): Promise<ReplayUploadBody> {
    if (typeof CompressionStream !== 'undefined') {
      const blob = new Blob([payload], { type: 'application/json' });
      const compressed = blob.stream().pipeThrough(new CompressionStream('gzip'));
      const gzBlob = await new Response(compressed).blob();
      return { body: gzBlob, contentEncoding: 'gzip', payloadBytes: gzBlob.size };
    }

    return { body: payload, payloadBytes: new Blob([payload]).size };
  }

  private replayMeta(events: unknown[]) {
    const metaEvent = [...events].reverse().find((event) => {
      return (
        typeof event === 'object' && event !== null && (event as { type?: unknown }).type === 4
      );
    }) as { data?: { href?: string; width?: number; height?: number } } | undefined;
    const href = metaEvent?.data?.href;
    let route: string | undefined;
    try {
      route = href ? new URL(href).pathname : undefined;
    } catch {
      route = undefined;
    }

    return {
      url: pathOnly(href),
      route,
      width: metaEvent?.data?.width,
      height: metaEvent?.data?.height,
    };
  }

  private safeObserve(type: string, callback: (entries: PerformanceEntryList) => void) {
    try {
      const observer = new PerformanceObserver((list) => {
        callback(list.getEntries());
      });
      observer.observe({ type, buffered: true } as PerformanceObserverInit);
      this.observers.push(observer);
    } catch {
      // Browser doesn't support this entry type — skip silently
    }
  }

  // The tab's session continues across reloads while it is neither idle nor past its cap.
  private resumeOrStartSession() {
    const now = Date.now();
    const stored = this.storedSession();
    if (
      stored &&
      now - stored.activeAt <= TelemetryService.SESSION_IDLE_MS &&
      now - stored.startedAt <= TelemetryService.SESSION_MAX_MS
    ) {
      this.#sessionStartedAt = stored.startedAt;
      this.storeSessionActivity(now);
      return stored.id;
    }
    return this.mintSession(now);
  }

  private storedSession() {
    try {
      const id = sessionStorage.getItem(TelemetryService.SESSION_KEY);
      const startedAt = Number(sessionStorage.getItem(TelemetryService.SESSION_STARTED_KEY));
      const activeAt = Number(sessionStorage.getItem(TelemetryService.SESSION_ACTIVE_KEY));
      if (!id || !Number.isFinite(startedAt) || !Number.isFinite(activeAt)) return null;
      return { id, startedAt, activeAt };
    } catch {
      return null;
    }
  }

  private mintSession(now: number) {
    const id = crypto.randomUUID();
    this.#sessionStartedAt = now;
    try {
      sessionStorage.setItem(TelemetryService.SESSION_KEY, id);
      sessionStorage.setItem(TelemetryService.SESSION_STARTED_KEY, String(now));
      sessionStorage.setItem(TelemetryService.SESSION_ACTIVE_KEY, String(now));
    } catch {
      // Without storage the session lasts the page load, as before.
    }
    return id;
  }

  private storeSessionActivity(now: number) {
    try {
      sessionStorage.setItem(TelemetryService.SESSION_ACTIVE_KEY, String(now));
    } catch {
      // Same fallback as mintSession.
    }
  }

  // Every click, key press, scroll and route change lands here: it keeps the session alive, or
  // starts the next one when the tab was idle or the session has run four hours. A route change
  // brings its own page view, so it asks for no landing.
  #noteActivity({ landing = true }: { landing?: boolean } = {}) {
    if (!this.config?.enabled) return;
    const now = Date.now();
    if (this.#sessionIdle || now - this.#sessionStartedAt > TelemetryService.SESSION_MAX_MS) {
      this.#startNextSession(now, landing);
    }
    this.storeSessionActivity(now);
    this.#armIdleTimer();
  }

  #armIdleTimer() {
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(() => this.#endSessionForIdle(), TelemetryService.SESSION_IDLE_MS);
  }

  // Nothing more is recorded for an idle tab; what it has is sent so the session closes whole.
  #endSessionForIdle() {
    this.#idleTimer = null;
    this.#sessionIdle = true;
    this.flush();
    if (this.config?.replayMode !== 'on-error' || this.replayErrorFlushed) {
      this.flushReplayBuffer();
    }
    this.stopSessionRecording();
    this.replayBuffer = [];
  }

  #startNextSession(now: number, landing: boolean) {
    this.#sessionIdle = false;
    this.sessionId = this.mintSession(now);
    this.replayChunkIndex = null;
    this.replayErrorFlushed = false;
    this.replayUploadFailures = 0;
    this.#landing = null;
    if (landing) {
      startNewTrace();
      // The route on screen is where this session begins; a page view gives it a landing.
      this.#landing = this.currentRoute
        ? {
            route: this.currentRoute,
            url: pathOnly(window.location.href),
            trace_id: currentTraceId(),
          }
        : null;
      this.push({
        event_type: 'page_view',
        ...(this.#landing ?? {}),
        page_view: { spa_transition: 1 },
      });
    }
    if (this.config?.recordSession && this.config.replayEndpoint) {
      void this.startSessionRecording();
    }
  }
}
